import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Download, Loader2, Video } from "lucide-react";
import { toast } from "sonner";
import { useCoachSessions, createSessionRoomLaunch, cancelSessionDirect, markNoShowDirect } from "@/lib/sessionQueries";
import { useMe } from "@/lib/rehearsalRoomQueries";
import { downloadSessionNotesPdf } from "@/lib/sessionToolsQueries";
import { formatDualTime } from "../session/SessionCard";
import TimeProposalComposer from "./TimeProposalComposer";
import CompleteSessionWizard from "./CompleteSessionWizard";

const VIEWS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "agenda", label: "Agenda" },
];

function rangeForView(view, anchor) {
  const start = new Date(anchor);
  if (view === "day") {
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }
  if (view === "week") {
    const dow = start.getDay();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    start.setDate(start.getDate() + diffToMonday);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  }
  // agenda — a rolling 30-day look-ahead from today
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 30);
  return { start, end };
}

const STATUS_LABEL = {
  booked: "Booked", pending_confirmation: "Pending TidyCal confirmation", cancelled: "Cancelled", no_show: "No-show", completed: "Completed",
};

/**
 * Master v5 Prompt 18 — coach-wide Day/Week/Agenda, across EVERY client/
 * Workspace (never scoped to one). São Paulo primary, participant local
 * time secondary (formatDualTime, same as the client-facing SessionCard).
 */
export default function CoachSessionsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const [view, setView] = useState("agenda");
  const [anchor, setAnchor] = useState(() => new Date());
  const [joiningId, setJoiningId] = useState(null);
  const [downloadingNotesId, setDownloadingNotesId] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);

  const { start, end } = useMemo(() => rangeForView(view, anchor), [view, anchor]);
  const { data, isPending, isError } = useCoachSessions({ rangeStart: start.toISOString(), rangeEnd: end.toISOString() });
  const items = useMemo(() => data?.items || [], [data]);

  function shiftAnchor(days) {
    setAnchor((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + days);
      return next;
    });
  }

  async function handleJoin(session) {
    setJoiningId(session.ses_id);
    try {
      const res = await createSessionRoomLaunch(session.workspace_id, session.ses_id);
      navigate(`/rehearsal-room/w/${session.workspace_id}/room/${res.launch_id}`);
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't join this Session");
    } finally {
      setJoiningId(null);
    }
  }
  async function handleDownloadNotes(session) {
    setDownloadingNotesId(session.ses_id);
    try {
      await downloadSessionNotesPdf(session.workspace_id, session.ses_id);
    } catch (err) {
      toast.error(err.message || "Couldn't download the notes PDF. Please try again.");
    } finally {
      setDownloadingNotesId(null);
    }
  }
  async function handleCancel(session) {
    if (!window.confirm("Cancel this Session?")) return;
    try {
      await cancelSessionDirect(session.workspace_id, session.ses_id, "Cancelled from the Sessions calendar");
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't cancel this Session");
    }
  }
  async function handleNoShow(session) {
    try {
      await markNoShowDirect(session.workspace_id, session.ses_id);
      qc.invalidateQueries({ queryKey: ["coach-sessions"] });
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't mark this Session as no-show");
    }
  }

  const grouped = useMemo(() => {
    const byDay = new Map();
    for (const s of items) {
      const key = new Date(s.starts_at).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo" });
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(s);
    }
    return Array.from(byDay.entries());
  }, [items]);

  return (
    <>
    <div data-testid="coach-sessions-page" className="max-w-4xl">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-serif text-3xl text-ink flex items-center gap-2"><CalendarDays size={26} /> Sessions</h1>
          <div className="flex items-center gap-1" data-testid="coach-sessions-view-tabs">
            {VIEWS.map((v) => (
              <button
                key={v.key} onClick={() => setView(v.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${view === v.key ? "bg-ink text-paper" : "text-ink/60 hover:bg-ink/5"}`}
                data-testid={`coach-sessions-view-${v.key}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {view !== "agenda" && (
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => shiftAnchor(view === "day" ? -1 : -7)} className="text-sm text-ink/60 hover:text-ink" data-testid="coach-sessions-prev-btn">← Previous</button>
            <span className="text-sm text-ink-soft">{start.toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo", month: "short", day: "numeric" })} – {new Date(end.getTime() - 1).toLocaleDateString("en-US", { timeZone: "America/Sao_Paulo", month: "short", day: "numeric" })}</span>
            <button onClick={() => shiftAnchor(view === "day" ? 1 : 7)} className="text-sm text-ink/60 hover:text-ink" data-testid="coach-sessions-next-btn">Next →</button>
          </div>
        )}

        {isPending && <p className="text-sm text-ink-soft" data-testid="coach-sessions-loading">Loading sessions…</p>}
        {isError && <p className="text-sm text-red-600" data-testid="coach-sessions-error">Couldn't load your Sessions calendar.</p>}
        {!isPending && items.length === 0 && <p className="text-sm text-ink-soft" data-testid="coach-sessions-empty">No sessions in this range.</p>}

        <div className="space-y-6" data-testid="coach-sessions-list">
          {grouped.map(([day, dayItems]) => (
            <div key={day}>
              <h2 className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-2">{day}</h2>
              <div className="space-y-2">
                {dayItems.map((s) => {
                  const { primary, secondary } = formatDualTime(s.starts_at, s.coach_timezone || "America/Sao_Paulo", me?.timezone);
                  // Master v5 Prompt 31 — the coach can enter their own
                  // Room any time before it ends/is cancelled, even
                  // days ahead (to check materials/Zoom) — never gated
                  // to the 30-minute join window that a client sees.
                  const canJoin = (s.status === "booked" || s.status === "pending_confirmation") && s.room_state !== "ended";
                  const canAct = (s.status === "booked" || s.status === "pending_confirmation") && s.room_state !== "ended";
                  const needsCompletion = s.status === "booked" && s.room_state === "ended";
                  const canComplete = s.status === "booked";
                  const canDownloadNotes = s.status === "completed" || s.room_state === "ended";
                  return (
                    <div key={s.ses_id} className="bg-white/60 rounded-xl border border-ink/10 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`coach-sessions-row-${s.ses_id}`}>
                      <div>
                        <p className="text-sm font-medium text-ink" data-testid={`coach-sessions-client-name-${s.ses_id}`}>{s.client_name || "Client"}</p>
                        <p className="text-xs text-ink/60">{primary}{secondary ? ` · ${secondary}` : ""}</p>
                        <span className="text-[11px] text-ink/45 uppercase">{STATUS_LABEL[s.status] || s.status}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/rehearsal-room/coach/w/${s.workspace_id}/client/${s.owner_member_id}/prep`)}
                          className="text-xs font-medium border border-ink/15 px-3 py-1.5 rounded-full text-ink hover:bg-ink/5"
                          data-testid={`coach-sessions-prepare-${s.ses_id}`}
                        >
                          Prepare
                        </button>
                        {canJoin && (
                          <button onClick={() => handleJoin(s)} disabled={joiningId === s.ses_id} className="flex items-center gap-1 text-xs font-medium bg-terracotta text-paper px-3 py-1.5 rounded-full hover:bg-terracotta/90 disabled:opacity-50" data-testid={`coach-sessions-start-${s.ses_id}`}>
                            {joiningId === s.ses_id ? <Loader2 size={12} className="animate-spin" /> : <Video size={12} />} Enter the session
                          </button>
                        )}
                        {canAct && (
                          <button onClick={() => setRescheduleTarget(s)} className="text-xs text-ink/60 hover:text-ink underline" data-testid={`coach-sessions-reschedule-${s.ses_id}`}>Reschedule</button>
                        )}
                        {canAct && (
                          <button onClick={() => handleCancel(s)} className="text-xs text-terracotta hover:underline" data-testid={`coach-sessions-cancel-${s.ses_id}`}>Cancel</button>
                        )}
                        {needsCompletion && (
                          <button onClick={() => handleNoShow(s)} className="text-xs text-ink/50 hover:text-ink underline" data-testid={`coach-sessions-no-show-${s.ses_id}`}>No-show</button>
                        )}
                        {canComplete && (
                          <button onClick={() => setCompleteTarget(s)} className="text-xs font-medium bg-ink text-paper px-3 py-1.5 rounded-full hover:bg-ink/85" data-testid={`coach-sessions-complete-${s.ses_id}`}>Complete Session</button>
                        )}
                        {canDownloadNotes && (
                          <button
                            onClick={() => handleDownloadNotes(s)}
                            disabled={downloadingNotesId === s.ses_id}
                            className="flex items-center gap-1 text-xs text-ink/70 hover:text-ink underline disabled:opacity-50"
                            data-testid={`coach-sessions-download-notes-${s.ses_id}`}
                          >
                            {downloadingNotesId === s.ses_id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download Notes (PDF)
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      {rescheduleTarget && (
        <TimeProposalComposer
          workspaceId={rescheduleTarget.workspace_id} ownerMemberId={rescheduleTarget.owner_member_id} rescheduleOfSessionId={rescheduleTarget.ses_id}
          onClose={() => setRescheduleTarget(null)}
        />
      )}
      {completeTarget && (
        <CompleteSessionWizard workspaceId={completeTarget.workspace_id} session={completeTarget} onClose={() => setCompleteTarget(null)} />
      )}
    </>
  );
}
